import { useEffect, useCallback, useRef } from "react";

/**
 * Options for keyboard shortcuts hook
 */
interface UseKeyboardShortcutsOptions {
  onArrowHoldStart: (direction: 1 | -1) => void;
  onArrowHoldEnd: () => void;
  onJumpToIndex: (index: number) => void;
  onTogglePlay: () => void;
  onEscape: () => void;
  totalPhotos: number;
  enabled: boolean;
}

/**
 * Hook for managing keyboard shortcuts in player mode
 *
 * Shortcuts:
 * - ArrowLeft (hold): Rewind with progressive acceleration
 * - ArrowRight (hold): Forward with progressive acceleration
 * - Space: Play/Pause toggle
 * - Home: Jump to first photo
 * - End: Jump to last photo
 * - Escape: Exit/close lightbox
 */
export function useKeyboardShortcuts(options: UseKeyboardShortcutsOptions) {
  const {
    onArrowHoldStart,
    onArrowHoldEnd,
    onJumpToIndex,
    onTogglePlay,
    onEscape,
    totalPhotos,
    enabled,
  } = options;

  // Track which arrow key is currently held
  const activeArrowRef = useRef<string | null>(null);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      // Ignore if focus is on input elements
      const target = e.target as HTMLElement;
      if (["INPUT", "TEXTAREA", "SELECT"].includes(target.tagName)) {
        return;
      }

      // Ignore if editing contenteditable
      if (target.isContentEditable) {
        return;
      }

      switch (e.key) {
        case "ArrowLeft":
          e.preventDefault();
          // Ignore key repeat — hold logic is timer-based
          if (activeArrowRef.current === "ArrowLeft") return;
          activeArrowRef.current = "ArrowLeft";
          onArrowHoldStart(-1);
          break;

        case "ArrowRight":
          e.preventDefault();
          if (activeArrowRef.current === "ArrowRight") return;
          activeArrowRef.current = "ArrowRight";
          onArrowHoldStart(1);
          break;

        case " ": // Space
          e.preventDefault();
          onTogglePlay();
          break;

        case "Home":
          e.preventDefault();
          onJumpToIndex(0);
          break;

        case "End":
          e.preventDefault();
          onJumpToIndex(totalPhotos - 1);
          break;

        case "Escape":
          onEscape();
          break;

        default:
          break;
      }
    },
    [onArrowHoldStart, onJumpToIndex, onTogglePlay, onEscape, totalPhotos]
  );

  const handleKeyUp = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === "ArrowLeft" || e.key === "ArrowRight") {
        if (activeArrowRef.current === e.key) {
          activeArrowRef.current = null;
          onArrowHoldEnd();
        }
      }
    },
    [onArrowHoldEnd]
  );

  useEffect(() => {
    if (!enabled) return;

    window.addEventListener("keydown", handleKeyDown);
    window.addEventListener("keyup", handleKeyUp);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("keyup", handleKeyUp);
    };
  }, [enabled, handleKeyDown, handleKeyUp]);
}
